import { useContext, useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ValidationContext } from './ValidationContext';
import { focusField } from '@/utils/focus';

type ValidationFooterProps = {
  scope?: number | string;
  onFocusError?: (field: string) => void;
};

const shakeVariants = {
  idle: { x: 0 },
  shake: {
    x: [0, -4, 4, -3, 2, 0],
    transition: { duration: 0.4, ease: 'easeInOut' as const },
  },
};

export function ValidationFooter({ scope, onFocusError }: ValidationFooterProps) {
  const ctx = useContext(ValidationContext);
  if (!ctx) throw new Error('ValidationFooter must be used within ValidationProvider');

  const { getActiveErrors, shakeKey } = ctx;
  const errors = getActiveErrors(scope);
  const [animKey, setAnimKey] = useState<'idle' | 'shake'>('idle');
  const shouldReduceMotion = useReducedMotion();

  // Track shakeKey changes — defer animation to next render so it fires
  // even when errors first appear via triggerShake (which batch-blurs fields)
  useEffect(() => {
    if (shakeKey > 0) {
      // Use rAF to ensure the footer has mounted before animating
      const frame = requestAnimationFrame(() => {
        setAnimKey('shake');
      });
      const timer = setTimeout(() => setAnimKey('idle'), 500);
      return () => {
        cancelAnimationFrame(frame);
        clearTimeout(timer);
      };
    }
  }, [shakeKey]);

  if (errors.length === 0) return null;

  const firstError = errors[0];

  const handleClick = () => {
    if (onFocusError) {
      onFocusError(firstError.field);
    } else {
      focusField(firstError.field);
    }
  };

  return (
    <div className="w-full" role="alert" aria-live="assertive">
      <motion.button
        type="button"
        onClick={handleClick}
        variants={shouldReduceMotion ? undefined : shakeVariants}
        animate={shouldReduceMotion ? undefined : animKey}
        className="font-sans text-xs text-destructive text-center hover:underline cursor-pointer w-full min-h-[44px] flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-[0.25rem]"
      >
        {firstError.message}
      </motion.button>
    </div>
  );
}
